/**
 * useModelPreferences - browser storage for explicit workload model routing.
 *
 * The closed route inventory lives in app-local non-React route policy code.
 * This hook owns preference storage and Rosetta Stone model catalog refresh only.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ModelConfig, LLMProvider } from '@ownyou/ab-testing';
import { useAuth } from '../contexts/AuthContext';
import {
  MODEL_ROUTE_DEFINITIONS,
  MODEL_ROUTE_PREFERENCES_V2_KEY,
  deriveModelIdPreferences,
  normalizeLlmProviderId,
  parseModelRoutePreferencesV2,
  serializeModelRoutePreferencesV2,
  upsertModelRouteSelection,
  type LegacyModelTier,
  type ModelIdPreferences,
  type ModelRouteDefinition,
  type ModelRouteId,
  type ModelRouteSelection,
  type ModelRouteSelections,
} from '../lib/llm-routing';
import {
  fetchAvailableModels,
  clearModelsCache,
  groupModelsByProvider,
} from '../utils/fetch-models';
import { emitRoutePreferenceAuditEvents } from '../lib/audit';

// TODO(route-executor): remove LegacyModelTier from this hook re-export with
// the remaining pre-executor compatibility types.
export type { LegacyModelTier, ModelRouteDefinition, ModelRouteId };

const LAST_REFRESH_KEY = 'ownyou_models_last_refresh';
const REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours
const ROUTE_SELECTIONS_SYNC_EVENT = 'ownyou-model-route-prefs-v2-changed';
const MODEL_CATALOG_SYNC_EVENT = 'ownyou-model-catalog-changed';

/**
 * Temporary compatibility projection for pre-executor callers.
 *
 * Deletion ledger:
 * - mission worker route preference payloads
 * - IAB classifier bundle execution
 * - Ikigai/profile source and synthesis summaries
 * - character summary worker calls
 *
 * New prompt-sending paths must not depend on this projection. Gate 03 Stage 2
 * preprocessing uses the explicit experiment provider/model selection instead.
 */
export type ModelPreferences = ModelIdPreferences;

export interface RouteSelectionInput {
  provider: string;
  model: string;
}

export interface UseModelPreferencesResult {
  /** All available models (dynamically fetched) */
  models: ModelConfig[];
  /** Models grouped by provider */
  modelsByProvider: Record<LLMProvider, ModelConfig[]>;
  /** Canonical provider-aware route selections. */
  routeSelections: ModelRouteSelections;
  /** Temporary routeId -> modelId projection for the deletion ledger above. */
  modelIdPreferences: ModelIdPreferences;
  /** Write the preferred provider/model pair immediately; React state reflects it on the next render. */
  setRouteSelection: (routeId: ModelRouteId, selection: RouteSelectionInput) => void;
  /** Clear the preferred provider/model pair immediately; React state reflects it on the next render. */
  clearRouteSelection: (routeId: ModelRouteId) => void;
  /** Read the last rendered provider-aware route selection. */
  getRouteSelection: (routeId: ModelRouteId) => ModelRouteSelection | null;
  /** Get the full ModelConfig for a route (null if not set or not found) */
  getModelForRoute: (routeId: ModelRouteId) => ModelConfig | null;
  /** Whether models are currently loading */
  isLoading: boolean;
  /** Last refresh timestamp (null if never) */
  lastRefreshedAt: number | null;
  /** Force refresh models from APIs */
  refreshModels: () => Promise<void>;
  /** Available explicit model routes */
  routes: readonly ModelRouteDefinition[];
}

function loadRouteSelections(): ModelRouteSelections {
  try {
    return parseModelRoutePreferencesV2(
      localStorage.getItem(MODEL_ROUTE_PREFERENCES_V2_KEY),
    ).routeSelections;
  } catch { /* ignore */ }
  return {};
}

function saveRouteSelections(routeSelections: ModelRouteSelections): void {
  try {
    localStorage.setItem(
      MODEL_ROUTE_PREFERENCES_V2_KEY,
      serializeModelRoutePreferencesV2(routeSelections),
    );
  } catch { /* ignore */ }
}

function getLastRefresh(): number | null {
  try {
    const stored = localStorage.getItem(LAST_REFRESH_KEY);
    return stored ? Number(stored) : null;
  } catch { return null; }
}

function setLastRefresh(ts: number): void {
  try {
    localStorage.setItem(LAST_REFRESH_KEY, String(ts));
  } catch { /* ignore */ }
}

function dispatchRouteSelectionsSync(): void {
  queueMicrotask(() => {
    window.dispatchEvent(new Event(ROUTE_SELECTIONS_SYNC_EVENT));
  });
}

function dispatchModelCatalogSync(models: ModelConfig[], refreshedAt: number): void {
  queueMicrotask(() => {
    window.dispatchEvent(new CustomEvent(MODEL_CATALOG_SYNC_EVENT, {
      detail: { models, refreshedAt },
    }));
  });
}

export function useModelPreferences(): UseModelPreferencesResult {
  const { isAuthenticated } = useAuth();
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [routeSelections, setRouteSelections] = useState<ModelRouteSelections>(loadRouteSelections);
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(getLastRefresh);
  const routeSelectionsRef = useRef(routeSelections);
  const prevAuthRef = useRef(isAuthenticated);

  const replaceRouteSelections = useCallback((next: ModelRouteSelections) => {
    routeSelectionsRef.current = next;
    setRouteSelections(next);
  }, []);

  const writeRouteSelections = useCallback((next: ModelRouteSelections) => {
    if (next === routeSelectionsRef.current) return;
    void emitRoutePreferenceAuditEvents({
      previous: routeSelectionsRef.current,
      next,
    });
    routeSelectionsRef.current = next;
    saveRouteSelections(next);
    setRouteSelections(next);
    dispatchRouteSelectionsSync();
  }, []);

  const doRefresh = useCallback(async (force: boolean) => {
    setIsLoading(true);
    try {
      if (force) clearModelsCache();
      const fetched = await fetchAvailableModels(force);
      setModels(fetched);
      const now = Date.now();
      setLastRefreshedAt(now);
      setLastRefresh(now);
      dispatchModelCatalogSync(fetched, now);
      console.log('[useModelPreferences] Refreshed', fetched.length, 'models');
    } catch (err) {
      console.error('[useModelPreferences] Refresh failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load + daily refresh check
  useEffect(() => {
    const last = getLastRefresh();
    const needsRefresh = !last || (Date.now() - last) > REFRESH_INTERVAL_MS;
    doRefresh(needsRefresh);
  }, [doRefresh]);

  // Refresh on login (auth false → true)
  useEffect(() => {
    if (isAuthenticated && !prevAuthRef.current) {
      doRefresh(true);
    }
    prevAuthRef.current = isAuthenticated;
  }, [isAuthenticated, doRefresh]);

  // Daily timer — check every hour if 24h has passed
  useEffect(() => {
    const interval = setInterval(() => {
      const last = getLastRefresh();
      if (!last || (Date.now() - last) > REFRESH_INTERVAL_MS) {
        doRefresh(true);
      }
    }, 60 * 60 * 1000); // check every hour
    return () => clearInterval(interval);
  }, [doRefresh]);

  // Sync V2 selections across hook instances via custom event.
  useEffect(() => {
    const handleSync = () => {
      replaceRouteSelections(loadRouteSelections());
    };
    window.addEventListener(ROUTE_SELECTIONS_SYNC_EVENT, handleSync);
    return () => window.removeEventListener(ROUTE_SELECTIONS_SYNC_EVENT, handleSync);
  }, [replaceRouteSelections]);

  // Sync refreshed model catalogs across hook instances in the same tab. This
  // keeps Settings route choices and worker route-plan resolution on one catalog.
  useEffect(() => {
    const handleModelCatalogSync = (event: Event) => {
      const detail = (event as CustomEvent<{ models?: ModelConfig[]; refreshedAt?: number }>).detail;
      if (!Array.isArray(detail?.models)) return;
      setModels(detail.models);
      if (typeof detail.refreshedAt === 'number' && Number.isFinite(detail.refreshedAt)) {
        setLastRefreshedAt(detail.refreshedAt);
      }
    };
    window.addEventListener(MODEL_CATALOG_SYNC_EVENT, handleModelCatalogSync);
    return () => window.removeEventListener(MODEL_CATALOG_SYNC_EVENT, handleModelCatalogSync);
  }, []);

  // Sync V2 selections across tabs/windows.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== MODEL_ROUTE_PREFERENCES_V2_KEY) return;
      replaceRouteSelections(loadRouteSelections());
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [replaceRouteSelections]);

  const setRouteSelection = useCallback((routeId: ModelRouteId, selection: RouteSelectionInput) => {
    const next = upsertModelRouteSelection(routeSelectionsRef.current, routeId, {
      provider: selection.provider,
      model: selection.model,
      updatedAt: Date.now(),
    });
    writeRouteSelections(next);
  }, [writeRouteSelections]);

  const clearRouteSelection = useCallback((routeId: ModelRouteId) => {
    writeRouteSelections(upsertModelRouteSelection(routeSelectionsRef.current, routeId, null));
  }, [writeRouteSelections]);

  const getRouteSelection = useCallback((routeId: ModelRouteId): ModelRouteSelection | null => {
    return routeSelections[routeId] ?? null;
  }, [routeSelections]);

  const modelIdPreferences = useMemo(
    () => deriveModelIdPreferences(routeSelections, models),
    [routeSelections, models],
  );

  const modelByProviderAndId = useMemo(() => {
    const byProviderAndId = new Map<string, ModelConfig>();
    for (const model of models) {
      const provider = normalizeLlmProviderId(model.provider);
      if (!provider) continue;
      byProviderAndId.set(`${provider}:${model.model}`, model);
    }
    return byProviderAndId;
  }, [models]);

  const getModelForRoute = useCallback((routeId: ModelRouteId): ModelConfig | null => {
    const selection = routeSelections[routeId];
    if (!selection) return null;
    return modelByProviderAndId.get(`${selection.provider}:${selection.model}`) ?? null;
  }, [routeSelections, modelByProviderAndId]);

  const refreshModels = useCallback(async () => {
    await doRefresh(true);
  }, [doRefresh]);

  const modelsByProvider = useMemo(
    () => groupModelsByProvider(models),
    [models],
  );

  return {
    models,
    modelsByProvider,
    routeSelections,
    modelIdPreferences,
    setRouteSelection,
    clearRouteSelection,
    getRouteSelection,
    getModelForRoute,
    isLoading,
    lastRefreshedAt,
    refreshModels,
    routes: MODEL_ROUTE_DEFINITIONS,
  };
}
